import { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, UserPlus, Trash2, Edit2, Check, Users, Loader, Eye, EyeOff, Camera } from "lucide-react";
import { useConfirm } from "./ConfirmDialog";
import { api } from "../api";

const ROLE_LABEL = { admin: "Administrador", empresa: "Empresa", auxiliar: "Auxiliar" };
const ROLE_COLOR = { admin: "#00A896", empresa: "#7C3AED", auxiliar: "#059669" };
const ROLE_BG = { admin: "#F0FDFA", empresa: "#F5F3FF", auxiliar: "#ECFDF5" };
const ROLE_BORDER = { admin: "#99E8DE", empresa: "#DDD6FE", auxiliar: "#A7F3D0" };

function RoleBadge({ role }) {
  return (
    <span style={{
      fontSize: "0.68rem",
      fontWeight: 600,
      padding: "3px 9px",
      borderRadius: 20,
      background: ROLE_BG[role],
      color: ROLE_COLOR[role],
      border: `1px solid ${ROLE_BORDER[role]}`,
      letterSpacing: "0.01em",
    }}>
      {ROLE_LABEL[role]}
    </span>
  );
}

function UserForm({ companies, onSave, onCancel, initial }) {
  const [form, setForm] = useState(initial || {
    name: "",
    username: "",
    password: "",
    email: "",
    role: "auxiliar",
    company_ids: [],
    can_move_documents: false,
  });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef();
  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const handlePhoto = async (file) => {
    if (!file || !initial?.id) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("photo", file);
      const updated = await api.uploadUserPhoto(initial.id, formData);
      if (updated.error) throw new Error(updated.error);
      setForm((prev) => ({ ...prev, photo: updated.photo }));
    } catch (err) {
      setError(err.message);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const toggleCompany = (id) => {
    setForm((prev) => ({
      ...prev,
      company_ids: prev.company_ids.includes(id)
        ? prev.company_ids.filter((item) => item !== id)
        : [...prev.company_ids, id],
    }));
  };

  const handleSave = async () => {
    if (!form.name || !form.username || (!initial && !form.password)) return;
    setLoading(true);
    setError(null);
    try {
      await onSave(form);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 6 }}
      transition={{ type: "spring", stiffness: 400, damping: 30 }}
      style={{
        background: "rgba(255,255,255,0.82)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        border: "1px solid rgba(0,0,0,0.08)",
        borderRadius: 14,
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        gap: 14,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.9)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <p style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)" }}>
          {initial ? "Editar usuario" : "Nuevo usuario"}
        </p>
        {initial?.id && (
          <>
            <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg" hidden onChange={(event) => handlePhoto(event.target.files[0])} />
            <div
              onClick={() => fileRef.current.click()}
              title="Cambiar foto"
              style={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                cursor: "pointer",
                flexShrink: 0,
                background: form.photo ? `url(${api.fileUrl(form.photo)}) center/cover` : "rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid rgba(0,0,0,0.1)",
              }}
            >
              {uploadingPhoto
                ? <Loader size={12} className="spin" />
                : (!form.photo && <Camera size={13} style={{ color: "var(--text-muted)" }} />)}
            </div>
          </>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div className="form-row">
          <label>Nombre completo *</label>
          <input className="form-input" value={form.name} onChange={set("name")} placeholder="Ej: Ana Garcia" />
        </div>
        <div className="form-row">
          <label>Usuario *</label>
          <input className="form-input" value={form.username} onChange={set("username")} placeholder="usuario123" />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div className="form-row">
          <label>Correo electrónico</label>
          <input className="form-input" type="email" value={form.email || ""} onChange={set("email")} placeholder="correo@empresa.com" />
        </div>
        <div className="form-row">
          <label>{initial ? "Nueva contrasena (opcional)" : "Contrasena *"}</label>
          <div style={{ position: "relative" }}>
            <input
              className="form-input"
              type={showPass ? "text" : "password"}
              value={form.password}
              onChange={set("password")}
              placeholder={initial ? "Dejar vacio para no cambiar" : "Contrasena"}
              style={{ paddingRight: 38 }}
            />
            <button
              type="button"
              onClick={() => setShowPass((prev) => !prev)}
              style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}
            >
              {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>
        <div className="form-row">
          <label>Rol *</label>
          <select className="form-input" value={form.role} onChange={set("role")}>
            <option value="auxiliar">Auxiliar</option>
            <option value="empresa">Empresa</option>
            <option value="admin">Administrador</option>
          </select>
        </div>
      </div>

      {form.role === "empresa" && (
        <div className="form-row">
          <label>Empresas asignadas</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
            {companies.map((company) => {
              const active = form.company_ids.includes(company.id);
              return (
                <button
                  key={company.id}
                  type="button"
                  onClick={() => toggleCompany(company.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "5px 12px",
                    borderRadius: 8,
                    fontSize: "0.78rem",
                    border: `1px solid ${active ? "var(--primary)" : "rgba(0,0,0,0.1)"}`,
                    background: active ? "#F0FDFA" : "rgba(0,0,0,0.03)",
                    color: active ? "var(--primary)" : "var(--text-muted)",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    transition: "all 0.15s",
                  }}
                >
                  {active && <Check size={12} />}
                  {company.name}
                </button>
              );
            })}
            {companies.length === 0 && <p style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>No hay empresas creadas aun.</p>}
          </div>
        </div>
      )}

      {form.role === "auxiliar" && (
        <div className="form-row">
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={!!form.can_move_documents}
              onChange={(event) => setForm((prev) => ({ ...prev, can_move_documents: event.target.checked }))}
            />
            Puede mover documentos entre carpetas
          </label>
        </div>
      )}

      {error && <p style={{ color: "#C53030", fontSize: "0.78rem", background: "#FEE2E2", padding: "8px 12px", borderRadius: 8 }}>{error}</p>}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", paddingTop: 4 }}>
        <button className="btn-secondary" onClick={onCancel}>Cancelar</button>
        <button className="btn-primary" onClick={handleSave} disabled={!form.name || !form.username || (!initial && !form.password) || loading}>
          {loading ? <><Loader size={14} className="spin" /> Guardando...</> : <><Check size={14} /> Guardar</>}
        </button>
      </div>
    </motion.div>
  );
}

export default function UsersPanel({ onClose }) {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const { confirm, ConfirmUI } = useConfirm();
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= 900);

  useEffect(() => {
    const syncMobile = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener("resize", syncMobile);
    return () => window.removeEventListener("resize", syncMobile);
  }, []);

  useEffect(() => {
    Promise.all([api.getUsers(), api.getCompanies()]).then(([usersResult, companiesResult]) => {
      setUsers(Array.isArray(usersResult) ? usersResult : []);
      setCompanies(Array.isArray(companiesResult) ? companiesResult : []);
      setLoading(false);
    });
  }, []);

  const handleCreate = async (form) => {
    const user = await api.createUser(form);
    if (user.error) throw new Error(user.error);
    setUsers((prev) => [...prev, user]);
    setCreating(false);
  };

  const handleEdit = async (id, form) => {
    const user = await api.updateUser(id, form);
    if (user.error) throw new Error(user.error);
    setUsers((prev) => prev.map((item) => item.id === id ? user : item));
    setEditingId(null);
  };

  const handleDelete = async (id, name) => {
    const ok = await confirm({
      title: "Eliminar usuario",
      message: `"${name}" sera eliminado y perdera acceso al sistema permanentemente.`,
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    await api.deleteUser(id);
    setUsers((prev) => prev.filter((user) => user.id !== id));
  };

  return ReactDOM.createPortal(
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.22 }}
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.32)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          zIndex: 99,
        }}
      />

      <motion.div
        initial={{ x: -20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: -20, opacity: 0 }}
        transition={{ type: "spring", stiffness: 380, damping: 34, mass: 0.75 }}
        style={{
          position: "fixed",
          left: isMobile ? 0 : "var(--sidebar-w)",
          top: 0,
          bottom: 0,
          width: isMobile ? "100vw" : 500,
          zIndex: 100,
          display: "flex",
          flexDirection: "column",
          background: "rgba(246,246,248,0.97)",
          backdropFilter: "blur(48px) saturate(200%)",
          WebkitBackdropFilter: "blur(48px) saturate(200%)",
          borderRight: isMobile ? "none" : "1px solid rgba(0,0,0,0.08)",
          boxShadow: isMobile ? "none" : "4px 0 40px rgba(0,0,0,0.14), 1px 0 0 rgba(0,0,0,0.06)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px 18px", borderBottom: "1px solid rgba(0,0,0,0.06)", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "linear-gradient(135deg, #F0FDFA, #CCFBF1)", border: "1px solid #99E8DE", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Users size={20} style={{ color: "var(--primary)" }} />
            </div>
            <div>
              <h2 style={{ fontSize: "1.05rem", fontWeight: 700, letterSpacing: "-0.4px" }}>Gestion de usuarios</h2>
              <p style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 1 }}>
                {users.length} usuario{users.length !== 1 ? "s" : ""} registrado{users.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={18} /></button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
              <Loader size={28} className="spin" opacity={0.4} />
            </div>
          ) : (
            <>
              <AnimatePresence>
                {users.map((user, index) => (
                  <motion.div
                    key={user.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ type: "spring", stiffness: 360, damping: 28, delay: index * 0.04 }}
                  >
                    {editingId === user.id ? (
                      <UserForm
                        companies={companies}
                        initial={{ ...user, password: "" }}
                        onSave={(form) => handleEdit(user.id, form)}
                        onCancel={() => setEditingId(null)}
                      />
                    ) : (
                      <div className="user-row">
                        <div className="user-avatar" style={{ background: user.photo ? `url(${api.fileUrl(user.photo)}) center/cover` : ROLE_COLOR[user.role], overflow: "hidden" }}>
                          {!user.photo && user.name.charAt(0).toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span style={{ fontWeight: 600, fontSize: "0.92rem", letterSpacing: "-0.2px" }}>{user.name}</span>
                            <RoleBadge role={user.role} />
                          </div>
                          <p style={{ fontSize: "0.76rem", color: "var(--text-muted)", marginTop: 2 }}>
                            @{user.username}
                            {user.email && <span> | {user.email}</span>}
                            {user.role === "empresa" && user.company_ids?.length > 0 && (
                              <span> | {user.company_ids.length} empresa{user.company_ids.length !== 1 ? "s" : ""} asignada{user.company_ids.length !== 1 ? "s" : ""}</span>
                            )}
                            {user.role === "auxiliar" && user.can_move_documents && (
                              <span> | puede mover documentos</span>
                            )}
                          </p>
                        </div>
                        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                          <button className="action-btn" onClick={() => setEditingId(user.id)}>
                            <Edit2 size={13} /> Editar
                          </button>
                          <button className="action-btn danger" onClick={() => handleDelete(user.id, user.name)}>
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>

              {users.length === 0 && !loading && (
                <div style={{ textAlign: "center", padding: "40px 0", color: "var(--text-muted)" }}>
                  <Users size={40} opacity={0.25} />
                  <p style={{ marginTop: 12, fontSize: "0.85rem" }}>No hay usuarios registrados</p>
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ padding: "16px 24px", borderTop: "1px solid rgba(0,0,0,0.06)", background: "rgba(245,245,247,0.6)", flexShrink: 0 }}>
          <AnimatePresence mode="wait">
            {creating ? (
              <motion.div
                key="form"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
              >
                <UserForm companies={companies} onSave={handleCreate} onCancel={() => setCreating(false)} />
              </motion.div>
            ) : (
              <motion.button
                key="add-btn"
                className="btn-secondary"
                onClick={() => setCreating(true)}
                style={{ width: "100%", justifyContent: "center", padding: "11px" }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <UserPlus size={16} /> Agregar usuario
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
      {ConfirmUI}
    </>,
    document.body
  );
}
